// #88. Shared by the post-payment flows in auth-flow/Payment.jsx.
//
// Both payment services resolve (never throw) to the AxiosError on a non-2xx, so
// "truthy" is not "succeeded". By the time these run Stripe has already charged
// the card, so the copy for anything we cannot confirm sends the customer to
// support rather than inviting a retry that could charge them twice.

import { SESSION_EXPIRED_MESSAGE } from './sessionEvents';

export const PAYMENT_UNCONFIRMED_MESSAGE =
  "Your payment went through but we couldn't confirm it. Please contact support before trying again.";

export function isPaymentConfirmed(result) {
  return result?.status === 200;
}

export function paymentRefusalMessage(result) {
  const status = result?.response?.status;
  const serverMessage = result?.response?.data?.message;

  // 4xx bodies with a `message` are written for the customer (the 409
  // "already subscribed" and "payment already used" refusals, 403s). 5xx bodies
  // and Joi's `{ error }` 400s are not.
  // A 401 here is an auth failure that survived the silent token refresh, and
  // the card has already been charged: never show it raw, and never as "log in
  // again" either. The response interceptor force-logs-out on it (a full page
  // load), so callers also `rememberUnconfirmedPayment()` for the login screen.
  if (status === 401) {
    return PAYMENT_UNCONFIRMED_MESSAGE;
  }

  if (status >= 400 && status < 500 && typeof serverMessage === 'string' && serverMessage.trim()) {
    return serverMessage;
  }
  return PAYMENT_UNCONFIRMED_MESSAGE;
}

// True when the session was lost and the app is force-logging-out (a full
// reload to /login that drops any snackbar): either a 401 that survived the
// refresh, or the request interceptor's own rejection when the proactive
// refresh failed before the request was even sent (a plain Error, no
// `response`). Both mean "remember the notice for the login screen".
export function isUnauthorized(result) {
  if (result?.response?.status === 401) return true;
  return result instanceof Error && !result.response && result.message === SESSION_EXPIRED_MESSAGE;
}

const NOTICE_KEY = 'paymentUnconfirmedNotice';
// Long enough to cover the forced-logout reload, short enough that a notice the
// reload never consumed (already on /login, stale refresh, account changed)
// cannot surface on a later, possibly different, login.
export const NOTICE_MAX_AGE_MS = 60 * 1000;

// The forced logout after a 401 is `window.location.assign('/login')`, which
// drops any snackbar. sessionStorage (tab-scoped, survives the reload) carries
// the copy to the login screen, which shows it once. Not part of
// clearStoredSession: that runs before the reload and would wipe it.
export function rememberUnconfirmedPayment() {
  try {
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ at: Date.now() }));
  } catch {
    // Storage unavailable: the snackbar shown at the call site is all we have.
  }
}

export function clearUnconfirmedPaymentNotice() {
  try {
    sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    // ignore
  }
}

export function takeUnconfirmedPaymentNotice() {
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    sessionStorage.removeItem(NOTICE_KEY);
    const at = Number(JSON.parse(raw)?.at);
    if (!Number.isFinite(at) || Date.now() - at > NOTICE_MAX_AGE_MS) {
      return null;
    }
    return PAYMENT_UNCONFIRMED_MESSAGE;
  } catch {
    return null;
  }
}
